import { Config } from '@backstage/config';
import { OAuth2 } from '@backstage/core-app-api';
import {
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  OAuthApi,
  oauthRequestApiRef,
  ProfileInfoApi,
  SessionApi,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  ScmAuth,
  scmAuthApiRef,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import { CloudAmqpConfig } from './configs';
import { analyticsApiRef } from '@backstage/core-plugin-api';
import { AzureApplicationInsightsAnalyticsApi } from '@internal/plugin-analytics-module-azure-appinsights';
import { SystemAddonsService } from './services/SystemAddonsService';
import {
  I18nDictionaryManagerApiClient,
  i18nDictionaryManagerApiRef,
} from '@internal/backstage-plugin-i18n-dictionary-manager';

export const azureDevopsAuthApiRef: ApiRef<
  OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'internal.auth.azure-devops',
});

export const cloudAmqpConfigRef: ApiRef<CloudAmqpConfig[]> = createApiRef({
  id: 'catalog.providers.cloud-amqp',
});

export const systemAddonsServiceApiRef: ApiRef<SystemAddonsService> = createApiRef({
  id: 'plugin.system-addons-service',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: azureDevopsAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        environment: configApi.getOptionalString('auth.environment'),
        provider: { id: 'ado', title: 'Azure DevOps', icon: () => null },
      }),
  }),

  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),

  createApiFactory({
    api: cloudAmqpConfigRef,
    deps: {
      configApi: configApiRef,
    },
    factory: ({ configApi }) => {
      return (
        configApi.getOptionalConfigArray('catalog.providers.cloud-amqp') || []
      ).map((item: Config) => ({
        connection: {
          url: item.getString('connection.url'),
          vhost: item.getString('connection.vhost'),
        },
      }));
    },
  }),

  createApiFactory({
    api: scmAuthApiRef,
    deps: {
      azureDevopsAuthApi: azureDevopsAuthApiRef,
    },
    factory: ({ azureDevopsAuthApi }) =>
      ScmAuth.merge(
        ScmAuth.forAuthApi(azureDevopsAuthApi, {
          host: 'dev.azure.com',
          scopeMapping: { default: [], repoWrite: [] },
        }),
      ),
  }),

  createApiFactory({
    api: analyticsApiRef,
    deps: { configApi: configApiRef, identityApi: identityApiRef },
    factory: ({ configApi, identityApi }) => {
      const azureConfig = configApi.getConfig('app.analytics.azure');
      return AzureApplicationInsightsAnalyticsApi.fromConfig(azureConfig, {
        identityApi,
      });
    },
  }),

  createApiFactory({
    api: systemAddonsServiceApiRef,
    deps: {
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
      discoveryApi: discoveryApiRef,
    },
    factory: ({ fetchApi, identityApi, discoveryApi }) =>
      new SystemAddonsService(fetchApi, identityApi, discoveryApi),
  }),

  createApiFactory({
    api: i18nDictionaryManagerApiRef,
    deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
    factory: ({ discoveryApi, fetchApi }) =>
      new I18nDictionaryManagerApiClient({ discoveryApi, fetchApi }),
  }),
];
